import type { ReactElement } from 'react';
import { useWatch } from 'react-hook-form';
import { ErrorCode } from '@gr/core/errors';
import type { PlanDto } from '@gr/core/accounts/plans/dto';
import { PLAN_LIMITS } from '@gr/core/accounts/plans/limits';
import { useT } from '../../../../app/di';
import { FieldRow, FieldRowActions, FieldRowGrow } from '../../../../ui-kit/FieldRow/FieldRow';
import { FormAlert, FormCheckbox, FormTextArea, FormTextField, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';
import { limitInputError, PLAN_FORM_FIELDS, planFormValues, type PlanFormValues } from '../model/plan-form';
import styles from './PlanForm.module.scss';

export interface PlanFormProps {
  id: string;
  /** Правка плана; без него — новый план. */
  plan?: PlanDto;
  onSubmit(values: PlanFormValues): Promise<ServerOutcome>;
}

/** PLAN-UC-1, PLAN-UC-1.CC-1: план — код, название, описание и лимиты; отправляется кнопкой с form={id}. */
export function PlanForm({ id, plan, onSubmit }: PlanFormProps): ReactElement {
  const { t } = useT();
  const { form, submit } = useServerForm<PlanFormValues>({ defaultValues: planFormValues(plan), fields: PLAN_FORM_FIELDS, onSubmit });
  const { control, formState } = form;
  const unlimited = useWatch({ control, name: 'unlimited' });
  const disabled = formState.isSubmitting;
  const required = { required: ErrorCode.FieldRequired };

  return (
    <form id={id} className="u-stack" onSubmit={submit} noValidate>
      <FormAlert form={form} />
      <FormTextField
        control={control}
        name="code"
        label={t('plans.form.code')}
        hint={t('plans.form.codeHint')}
        rules={required}
        required
        autoComplete="off"
        disabled={disabled || plan !== undefined}
      />
      <FormTextField control={control} name="name" label={t('plans.form.name')} rules={required} required disabled={disabled} />
      <FormTextArea control={control} name="description" label={t('plans.form.description')} rows={2} disabled={disabled} />
      <fieldset className={styles.limits}>
        <legend className={styles.legend}>{t('plans.form.limitsTitle')}</legend>
        {PLAN_LIMITS.map((limit) => (
          <FieldRow key={limit} from="sm">
            <FieldRowGrow>
              <FormTextField
                control={control}
                name={`limits.${limit}`}
                label={t(`plans.limits.${limit}`)}
                hint={t(`plans.limitHints.${limit}`)}
                inputMode="numeric"
                rules={{ validate: (value: unknown, values: PlanFormValues) => limitInputError(typeof value === 'string' ? value : '', values.unlimited[limit]) ?? true }}
                disabled={disabled || unlimited[limit]}
              />
            </FieldRowGrow>
            <FieldRowActions>
              <FormCheckbox control={control} name={`unlimited.${limit}`} label={t('plans.form.unlimited')} disabled={disabled} />
            </FieldRowActions>
          </FieldRow>
        ))}
      </fieldset>
    </form>
  );
}
